<template>
    <CardToggle :title="$t('player.layout.header')">
        <div class="space-y-5">
            <FormToggleSm :title="$t('player.layout.display_header')" v-model="player.showHeader" />

            <FormFiles
                :title="$t('player.layout.header_logo')"
                :labels="{
                    clickToUpload: $t('shared.uploader.click_to_upload'),
                    dragAndDrop: $t('shared.uploader.drag_and_drop'),
                    fileInfo: $t('shared.uploader.file_details'),
                }"
                :maxSize="10000"
                :errorMessages="{
                    invalidSize: $t('shared.invalid.file_size_over'),
                    invalidType: $t('shared.invalid.file_type'),
                }"
                :single-file="true"
                v-model="files"
                @fileSelected="uploadImage"
                @fileRemoved="removeImage"
            />

            <FormDivider />

            <FormTitle
                :title="$t('player.layout.header_links')"
                :help="$t('player.layout.header_links_info')"
            />

            <draggable v-model="headersList" item-key="id" @change="order">
                <template #item="{ element }">
                    <div class="relative mt-2 group" v-if="!element._deleted">
                        <LinkCard
                            class="mb-3 ct-drag-item"
                            :link="element"
                            @edit="$emit('editLink', element)"
                            @delete="askAndRemoveLink(element)"
                            @duplicate="cloneHeader(element)"
                            @click="$emit('editLink', element)"
                        />
                        <DragDropIcon
                            class="absolute top-1/2 -left-3 -translate-y-1/2 ct-fg-4 opacity-0 group-hover:opacity-100"
                        />
                    </div>
                </template>
            </draggable>

            <CardEmpty
                class="w-full"
                v-if="!notDeletedHeaders.length"
                :title="$t('player.empty_headers.title')"
                :icon="emptyIcon"
                :ctaLabel="$t('player.empty_headers.cta')"
                @add="createEmpty"
            />

            <ButtonSecondaryLg v-else class="w-full" @click="createEmpty">
                <LinkAddIcon />
                <span>{{ $t("player.layout.new_link") }}</span>
            </ButtonSecondaryLg>
        </div>
    </CardToggle>
</template>

<script>
import { markRaw } from "vue";
import draggable from "vuedraggable";
import { mapState, mapMutations, mapActions } from "vuex";

import { CardToggle, CardEmpty } from "@easy/ui/cards";
import { FormToggleSm, FormFiles, FormDivider, FormTitle, makeFileModel } from "@easy/ui/forms";
import { ButtonSecondaryLg } from "@easy/ui/buttons";
import { LinkAddIcon, DragDropIcon, LinkChainIcon } from "@easy/ui/icons";
import LinkCard from "./LinkCard.vue";
import { PLAYER_HEADER_TYPES, PLAYER_HEADER_SIDES } from "@/apps/player/services/players.service";

export default {
    components: {
        draggable,
        CardToggle,
        FormToggleSm,
        FormFiles,
        FormDivider,
        FormTitle,
        ButtonSecondaryLg,
        LinkAddIcon,
        DragDropIcon,
        LinkCard,
        CardEmpty,
    },
    emits: ["editLink", "addLink"],
    data() {
        return {
            files: [],
        };
    },
    watch: {
        player: {
            handler(player, oldPlayer) {
                if (!oldPlayer?.logo && player?.logo) {
                    this.files = [
                        makeFileModel({
                            url: player.logo,
                        }),
                    ];
                }
            },
            immediate: true,
            deep: true,
        },
    },
    computed: {
        ...mapState("player", ["player", "headers"]),
        headersList: {
            get() {
                return this.headers;
            },
            set(value) {
                this.setHeaders(value);
            },
        },
        notDeletedHeaders() {
            return this.headers.filter((header) => !header._deleted);
        },
        emptyIcon() {
            return markRaw(LinkChainIcon);
        },
    },
    methods: {
        ...mapActions("modals", ["showConfirmModal"]),
        ...mapMutations("player", ["setHeaders", "addHeader"]),
        ...mapActions("player", ["uploadLogo", "cloneHeader", "removeHeader"]),
        removeImage() {
            this.player.logo = "";
        },
        uploadImage($event) {
            this.uploadLogo({ file: $event });
        },
        order() {
            this.setHeaders();
        },
        toggleCheck(key, value) {
            if (value) {
                this.player[key] = value;
            } else {
                this.player[key] = !this.player[key];
            }
        },
        updateHeader(header) {
            if (!header._created && !header._deleted) {
                header._changed = true;
            }
        },
        async askAndRemoveLink(link) {
            const decision = await this.showConfirmModal({
                title: this.$t("player.headers.delete_link_confirm"),
                description: this.$t("player.headers.delete_link_message"),
                ctaLabel: this.$t("shared.delete"),
            });
            if (decision) {
                this.removeHeader(link);
            }
        },
        createEmpty() {
            this.$emit("addLink", {
                type: PLAYER_HEADER_TYPES.button,
                side: PLAYER_HEADER_SIDES.left,
            });
        },
    },
};
</script>
